/** Object statuses that are considered active for listing filters (excludes DEACTIVATED). */
export const ACTIVE_OBJECT_STATUSES = [
  "PENDING",
  "ACTIVE",
  "PENDING_ACTIVATION",
  "PENDING_DEACTIVATION",
  "ERROR"
] as const;

export type ActiveObjectStatus = (typeof ACTIVE_OBJECT_STATUSES)[number];

export type ObjectStatusValue = string | number | null | undefined;

export const OBJECT_STATUS_EXACT_TYPE = 1;

const DEACTIVATED_STATUS_NUMBER = 4;
const DEACTIVATED_STATUS_NAME = "DEACTIVATED";

/** Color tokens used in object icon URLs (aligned with core ColorsNames). */
const ICON_COLOR_TOKENS = [
  "red",
  "blue",
  "green",
  "purple",
  "orange",
  "gray",
  "grey"
] as const;

const COLOR_TOKEN_PATTERN = new RegExp(
  `-(${ICON_COLOR_TOKENS.join("|")})(?=\\.|$)`,
  "i"
);

export const normalizeObjectStatus = (
  status: ObjectStatusValue
): string | number | undefined => {
  if (status === null || status === undefined || status === "") {
    return undefined;
  }

  if (typeof status === "number" && !Number.isNaN(status)) {
    return status;
  }

  if (typeof status === "string") {
    const trimmed = status.trim();
    if (!trimmed) return undefined;

    const asNumber = Number(trimmed);
    if (!Number.isNaN(asNumber) && trimmed === String(asNumber)) {
      return asNumber;
    }

    return trimmed.toUpperCase();
  }

  return undefined;
};

export const isInactiveObject = (status: ObjectStatusValue): boolean => {
  const normalized = normalizeObjectStatus(status);

  if (normalized === undefined) return false;

  if (typeof normalized === "number") {
    return normalized === DEACTIVATED_STATUS_NUMBER;
  }

  return normalized === DEACTIVATED_STATUS_NAME;
};

export const buildActiveStatusWhereClauses = (
  showInactiveObjects: boolean
): Array<{ field: string; value: string; type: number }> => {
  if (showInactiveObjects) return [];

  return ACTIVE_OBJECT_STATUSES.map((status) => ({
    field: "Status",
    value: status,
    type: OBJECT_STATUS_EXACT_TYPE
  }));
};

export const buildObjectStatusLookup = (
  objects:
    | Array<{ uniqueId?: string; status?: ObjectStatusValue }>
    | null
    | undefined
): Record<string, ObjectStatusValue> => {
  if (!objects?.length) return {};

  return objects.reduce<Record<string, ObjectStatusValue>>((acc, object) => {
    if (object?.uniqueId) {
      acc[object.uniqueId] = object.status;
    }
    return acc;
  }, {});
};

/** Safely rewrites an object icon URL to the gray/grey color variant. */
export const getInactiveIconUrl = (url: string | undefined | null): string => {
  if (!url) return "";

  if (COLOR_TOKEN_PATTERN.test(url)) {
    return url.replace(COLOR_TOKEN_PATTERN, "-gray");
  }

  // Fallback: insert gray before the file extension when no known color token exists.
  return url.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, `-gray$1$2`);
};

export const resolveObjectIconUrl = (
  url: string | undefined | null,
  status: ObjectStatusValue
): string => {
  if (!url) return "";
  return isInactiveObject(status) ? getInactiveIconUrl(url) : url;
};

export type EnrichedMapMarker = {
  uniqueId?: string;
  iconUrl?: string;
  status?: ObjectStatusValue;
  isInactive?: boolean;
  [key: string]: unknown;
};

export const enrichMarkersWithObjectStatus = <T extends EnrichedMapMarker>(
  markers: T[],
  statusLookup: Record<string, ObjectStatusValue>,
  options?: { showInactiveObjects?: boolean }
): T[] => {
  const showInactiveObjects = options?.showInactiveObjects ?? false;

  return markers
    .map((marker) => {
      const uniqueId = marker.uniqueId;
      if (!uniqueId) return marker;

      const status =
        marker.status !== undefined && marker.status !== null
          ? marker.status
          : statusLookup[uniqueId];
      const inactive = isInactiveObject(status);

      return {
        ...marker,
        status,
        isInactive: inactive,
        iconUrl: resolveObjectIconUrl(marker.iconUrl, status)
      };
    })
    .filter((marker) => showInactiveObjects || !marker.isInactive);
};
